import { HttpClient, PaginatedResponse, robotoHeaders } from "@/shared/http";

import {
  CommentEntityType,
  CommentRecord,
  CreateCommentRequest,
} from "./CommentRecord";

interface Options {
  abortSignal?: AbortSignal;
  orgId?: string;
}

interface UpdateCommentRequest {
  comment_text: string;
}

export class CommentService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  /**
   * Get comments for a specific entity (dataset, file, action, etc.)
   */
  public async getCommentsForEntity(
    entityType: CommentEntityType,
    entityId: string,
    options?: Partial<Options & { pageToken?: string }>,
  ): Promise<PaginatedResponse<CommentRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const encodedEntityId = encodeURIComponent(entityId);
    const requestUrl = this.#httpClient.constructUrl(
      `v1/comments/${entityType}/${encodedEntityId}`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<PaginatedResponse<CommentRecord>>();
  }

  /**
   * Get comments created by a specific user
   */
  public async getCommentsForUser(
    userId: string,
    options?: Partial<Options & { pageToken?: string }>,
  ): Promise<PaginatedResponse<CommentRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const encodedUserId = encodeURIComponent(userId);
    const requestUrl = this.#httpClient.constructUrl(
      `v1/comments/user/${encodedUserId}`,
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<PaginatedResponse<CommentRecord>>();
  }

  /**
   * Get recent comments across the organization
   */
  public async getRecentComments(
    options?: Partial<Options & { pageToken?: string }>,
  ): Promise<PaginatedResponse<CommentRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken) {
      searchParams.append("page_token", options.pageToken);
    }

    const requestUrl = this.#httpClient.constructUrl(
      "v1/comments/recent",
      searchParams,
    );

    const response = await this.#httpClient.get(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<PaginatedResponse<CommentRecord>>();
  }

  /**
   * Create a new comment
   */
  public async createComment(
    request: CreateCommentRequest,
    options?: Partial<Options>,
  ): Promise<CommentRecord> {
    const requestUrl = this.#httpClient.constructUrl("v1/comments");

    const response = await this.#httpClient.post(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(request),
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<CommentRecord>();
  }

  /**
   * Update an existing comment
   */
  public async updateComment(
    commentId: string,
    commentText: string,
    options?: Partial<Options>,
  ): Promise<CommentRecord> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/comments/${commentId}`,
    );

    const updateRequest: UpdateCommentRequest = {
      comment_text: commentText,
    };

    const response = await this.#httpClient.put(requestUrl, {
      signal: options?.abortSignal,
      body: JSON.stringify(updateRequest),
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.json<CommentRecord>();
  }

  /**
   * Delete a comment
   */
  public async deleteComment(
    commentId: string,
    options?: Partial<Options>,
  ): Promise<void> {
    const requestUrl = this.#httpClient.constructUrl(
      `v1/comments/${commentId}`,
    );

    const response = await this.#httpClient.delete(requestUrl, {
      signal: options?.abortSignal,
      headers: robotoHeaders({ orgId: options?.orgId }),
    });

    return response.throwIfError();
  }
}
